import {
  Box,
  styled,
  Table as MUITable,
  Grid,
  TableRow,
  TableCell,
  Button,
  tableCellClasses,
  Checkbox,
  TableContainer,
  TableHead,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Typography,
  Paper,
  TextField,
  Collapse,
  Table,
  IconButton
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PageHeader from '../../../../components/PageHeader';
import { api } from '../../../../api';
import { UserPagePermission } from '../../../../schema/permission';
import { UserRole } from '../../../../schema/user';
import menuItems, { MenuItemType } from '../../../../menu-items';
import { Link, useParams } from 'react-router-dom';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const UserRoleEdit: React.FC = () => {
  const { user_role_id } = useParams();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [roleId, setRoleId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState<string>('');
  const [roleDescription, setRoleDescription] = useState<string>('');
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState<boolean>(false);

  const [pagePermission, setPagePermission] = useState<Record<string, UserPagePermission>>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadData = () => {
    if (!user_role_id) {
      return;
    }
    api.get<UserRole>(`/user_role/${user_role_id}`).then((res) => {
      const { data } = res;
      setRoleId(data.id);
      setRoleName(data.role_name);
      setRoleDescription(data.role_description);
    });

    api.get<UserPagePermission[]>(`/permission/${user_role_id}`).then((res) => {
      const { data } = res;
      const temp: Record<string, UserPagePermission> = {};
      const addToTemp = (item: MenuItemType) => {
        temp[item.id] = {
          page_id: item.id,
          user_role_id: parseInt(user_role_id),
          is_allowed: false
        };
        if (item.children) {
          item.children.forEach((element) => {
            addToTemp(element);
          });
        }
        return;
      };
      menuItems.items.forEach((item) => {
        addToTemp(item);
      });
      data.forEach((permit) => {
        if (permit.page_id) {
          if (permit.page_id in temp) {
            temp[permit.page_id] = {
              page_id: permit.page_id,
              user_role_id: permit.user_role_id,
              is_allowed: permit.is_allowed
            };
          } else {
            temp[permit.page_id] = {
              page_id: permit.page_id,
              user_role_id: permit.user_role_id,
              is_allowed: permit.is_allowed
            };
          }
        }
      });
      setPagePermission(temp);
    });
  };

  const openSuccessDialog = () => {
    setIsOpenSuccessDialog(true);
    setTimeout(() => {
      setIsOpenSuccessDialog(false);
    }, 1000);
  };

  const handleSaveRoleInfo = (event: React.FormEvent) => {
    event.preventDefault();
    if (!user_role_id) {
      return;
    }
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    api
      .put(`/user_role/${user_role_id}`, {
        role_description: roleDescription
      })
      .then(() => {
        openSuccessDialog();
      })
      .finally(() => setIsLoading(false));
  };
  const handleSavePermission = (event: React.FormEvent) => {
    event.preventDefault();
    if (!user_role_id) {
      return;
    }
    if (isLoading) {
      return;
    }
    const data: UserPagePermission[] = [];
    for (const page_id in pagePermission) {
      data.push({
        page_id: page_id,
        user_role_id: parseInt(user_role_id),
        is_allowed: !!pagePermission[page_id].is_allowed
      });
    }
    setIsLoading(true);
    api
      .post(`/permission`, data)
      .then(() => {
        openSuccessDialog();
        loadData();
      })
      .finally(() => setIsLoading(false));
  };

  const handleChangePermission = (page_id: string) => {
    const temp = { ...pagePermission };

    if (!user_role_id) {
      return;
    }
    if (!(page_id in temp)) {
      temp[page_id] = {
        page_id: page_id,
        user_role_id: parseInt(user_role_id),
        is_allowed: true
      };
    } else {
      temp[page_id].is_allowed = !temp[page_id].is_allowed;
    }

    setPagePermission(temp);
  };

  const render = (
    menu: MenuItemType,
    is_parent_allowed: boolean,
    permission: Record<string, UserPagePermission>,
    order: number
  ): JSX.Element | null => {
    if (!permission[menu.id]) {
      return null;
    }

    const parent_allowed = is_parent_allowed && permission[menu.id].is_allowed;

    const getBgColor = () => {
      if (menu.type == 'hidden') {
        return '#D3D3D3';
      } else if (order === 1) {
        return '#14B8A690';
      } else {
        return '#c2eddf';
      }
    };

    return (
      <>
        <TableRow
          key={menu.id}
          sx={{
            bgcolor: getBgColor()
          }}
        >
          <TableCell>
            {menu.children && (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen({ ...open, [menu.id]: !open[menu.id] })}
              >
                {open[menu.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
          <TableCell>{menu.title}</TableCell>
          <TableCell>
            <Typography variant="caption" color={menu.type === 'hidden' ? 'GrayText' : '#000'}>
              {menu.type === 'hidden' ? 'ปิดการใช้งาน' : 'เปิดใช้งาน'}
            </Typography>
          </TableCell>
          <TableCell align="center">
            <Checkbox
              checked={parent_allowed}
              onChange={() => handleChangePermission(menu.id)}
              disabled={is_parent_allowed === false}
              indeterminate={is_parent_allowed === false}
            />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open[menu.id]} timeout="auto" unmountOnExit>
              <Table sx={{ p: 0, backgroundColor: '#D3D3D3' }}>
                {menu.children?.map((item) => render(item, parent_allowed, permission, order + 1))}
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Container>
      <PageHeader text="แก้ไขข้อมูล User Role" />
      <Button component={Link} to={`/user-role-management`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        จัดการการเข้าถึง
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">{'ข้อมูล User Role'}</Typography>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Role ID"
                variant="outlined"
                placeholder="Role ID"
                value={roleId ? roleId : ''}
                InputProps={{ readOnly: true }}
                disabled={true}
                sx={{
                  '.Mui-disabled': {
                    color: 'black',
                    opacity: 1,
                    '-webkit-text-fill-color': 'black'
                  },
                  '.MuiInputLabel-root': {
                    color: '#65748B',
                    '-webkit-text-fill-color': '#65748B'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Role Name"
                variant="outlined"
                placeholder="Role Name"
                value={roleName}
                InputProps={{ readOnly: true }}
                disabled={true}
                sx={{
                  '.Mui-disabled': {
                    color: 'black',
                    opacity: 1,
                    '-webkit-text-fill-color': 'black'
                  },
                  '.MuiInputLabel-root': {
                    color: '#65748B',
                    '-webkit-text-fill-color': '#65748B'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Role Description"
                variant="outlined"
                placeholder="Role Description"
                onChange={(e) => setRoleDescription(e.target.value ? e.target.value : '')}
                value={roleDescription}
                required
              />
            </Grid>
            <Grid container columns={2} spacing={2} sx={{ mt: 0 }} direction={'row-reverse'}>
              <Grid item>
                <Button onClick={handleSaveRoleInfo} variant="contained" color="primary">
                  บันทึก
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6">{`ข้อมูลการเข้าถึง`}</Typography>
        <Box sx={{ mt: 2, width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <MUITable sx={{ minWidth: 650, maxHeight: 440 }} size="medium" stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell></StyledTableCell>
                  <StyledTableCell align="center">{'Pages'}</StyledTableCell>
                  <StyledTableCell>สถานะ</StyledTableCell>
                  <StyledTableCell align="center" width="40%">
                    การอนุญาต
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menuItems.items.map((item) => {
                  return render(item, true, pagePermission, 1);
                })}
              </TableBody>
            </MUITable>
          </TableContainer>
          <Grid container columns={2} spacing={2} direction={'row-reverse'}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePermission}
                sx={{ marginTop: 4 }}
              >
                บันทึก
              </Button>
            </Grid>
          </Grid>
          <Dialog open={isOpenSuccessDialog} onClose={() => setIsOpenSuccessDialog(false)}>
            <DialogTitle>{'Success'}</DialogTitle>
            <DialogContent>
              <DialogContentText>อัพเดทข้อมูลสำเร็จ</DialogContentText>
            </DialogContent>
          </Dialog>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserRoleEdit;

const Container = styled('div')`
  padding: 24px;
`;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  }
}));
